// ============================================================================
// FacilityPicker — searchable single-select over active facilities. Used by
// the ad-hoc stop modal and the template "add assignment" form. Mobile-first:
// 16px input (no iOS zoom), big tap rows.
// ============================================================================

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { RagDot } from '../../../shared/components';
import { ServiceStateChip } from '../../account-management/stop-service-parts';
import type { FacilityOption } from '../hooks/use-am-routes-data';

export interface FacilityPickerProps {
  options: FacilityOption[];
  value: string | null;
  onChange: (facilityId: string | null, facilityName: string | null) => void;
  placeholder?: string;
}

export default function FacilityPicker({
  options,
  value,
  onChange,
  placeholder = 'Search facilities…',
}: FacilityPickerProps) {
  const [query, setQuery] = useState('');
  const selected = options.find((o) => o.facility_id === value) ?? null;

  // AFB-0014 — a query must NEVER silently drop a matching facility. The old
  // `.slice(0, 30)` capped matches at 30 sorted by name, which hid e.g.
  // "DaVita - Wyandotte Central" (#46 of 50 DaVita sites) when an AM searched
  // "DaVita". The active-facility list is small (≤ ~200), so render every
  // match for a query; only the empty (browse) state is capped, with a hint.
  const EMPTY_BROWSE_LIMIT = 50;
  const { matches, browseTruncated } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return {
        matches: options.slice(0, EMPTY_BROWSE_LIMIT),
        browseTruncated: options.length > EMPTY_BROWSE_LIMIT,
      };
    }
    return {
      matches: options.filter((o) =>
        (o.facility_name ?? '').toLowerCase().includes(q),
      ),
      browseTruncated: false,
    };
  }, [query, options]);

  return (
    <div>
      {selected && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-md border border-tcs-border bg-tcs-surface px-3 py-2 text-sm">
          <span className="flex items-center gap-2 text-tcs-text">
            <RagDot status={selected.account_status_rag} />
            {selected.facility_name ?? selected.facility_id}
            <ServiceStateChip state={selected.service_state} size="sm" />
          </span>
          <button
            type="button"
            onClick={() => onChange(null, null)}
            className="text-xs font-medium text-tcs-text-secondary hover:text-tcs-text"
          >
            Change
          </button>
        </div>
      )}
      {!selected && (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              autoFocus
              className="w-full rounded-md border border-tcs-border bg-tcs-field py-2 pl-9 pr-3 text-base shadow-inner focus:border-tcs-accent focus:outline-none"
            />
          </div>
          <ul className="mt-2 max-h-60 overflow-auto rounded-md border border-tcs-border">
            {matches.length === 0 && (
              <li className="px-3 py-3 text-sm text-tcs-text-muted">
                No facilities match “{query}”.
              </li>
            )}
            {matches.map((o) => (
              <li key={o.facility_id}>
                <button
                  type="button"
                  onClick={() => onChange(o.facility_id, o.facility_name)}
                  className="flex w-full items-center gap-2 border-b border-tcs-border px-3 py-2.5 text-left text-sm last:border-0 hover:bg-tcs-bg-muted"
                >
                  <RagDot status={o.account_status_rag} />
                  <span className="truncate text-tcs-text">
                    {o.facility_name ?? o.facility_id}
                  </span>
                  <ServiceStateChip state={o.service_state} size="sm" />
                </button>
              </li>
            ))}
            {browseTruncated && (
              <li className="border-t border-tcs-border px-3 py-2 text-xs text-tcs-text-muted">
                Showing the first {EMPTY_BROWSE_LIMIT}. Type to search all{' '}
                {options.length} facilities.
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}
